import { cn } from "@utils/tw"
import { Link } from "eddev/routing"
import { animate } from "motion"
import { AnimatePresence, LayoutGroup, motion, MotionValue, transformValue, useMotionValue } from "motion/react"
import { MouseEvent, ReactNode, useEffect, useId, useState } from "react"

type TabItem = {
  label: ReactNode
  value: string
  href?: string
  onClick?: (e: MouseEvent) => void
  progress?: MotionValue<number>
}

type Props = {
  items: TabItem[]
  value: string
  onHoverItem?: (string: string | null) => void
  onSelect?: (string: string) => void
  defaultSelectFirstItem?: boolean
  classNames?: {
    base?: string
    tab?: string
  }
}

/**
 * Animated tabs.
 *
 * Uses Motion's layoutId for animating the indicator position.
 */
export function TabControl(props: Props) {
  const id = useId()

  const getSelectedIndex = () => {
    return Math.max(
      props.defaultSelectFirstItem ? 0 : -1,
      props.items.findIndex((item) => item.value === props.value),
    )
  }

  /**
   * The indicator might be shown temporarily on an item which isn't actually active.
   * When no item is selected, we keep the indicatorIndex at it's previous value until the indicator finishes fading out
   */
  const [indicatorIndex, setIndicatorIndex] = useState(-1)
  const actualIndex = getSelectedIndex()
  const hasActive = actualIndex === indicatorIndex && indicatorIndex !== -1

  useEffect(() => {
    setIndicatorIndex((current) => {
      const nextIndex = getSelectedIndex()
      return nextIndex > -1 ? nextIndex : current
    })
  }, [props.value])

  /**
   * Animate the indicatorOpacity based on whether a tab is active
   * We manage this value separately, since technically the indicator element is destroyed/recreated whenever the index changes
   */
  const indicatorOpacity = useMotionValue(0)
  useEffect(() => {
    if (hasActive) {
      animate(indicatorOpacity, 1.0, {
        duration: 0.3,
      })
    } else {
      animate(indicatorOpacity, 0.0, {
        duration: 0.3,
        delay: 0.4,
      }).then(() => {
        setIndicatorIndex(-1)
      })
    }
  }, [hasActive])

  return (
    <LayoutGroup id={id}>
      <motion.ul
        layoutRoot
        className={cn(
          "flex border border-white/15 bg-black/40 backdrop-blur-md isolate p-2 rounded-md will-change-transform",
          props.classNames?.base,
        )}
      >
        {props.items.map((item, i) => {
          const active = i === indicatorIndex
          return (
            <motion.li key={i} className="flex">
              <Link
                className={cn(
                  "relative inline-flex text-center items-center justify-center px-8 flex-1 type-label-s md:type-label-l h-8 group",
                  props.classNames?.tab,
                )}
                href={item.href}
                onClick={(e) => {
                  item.onClick?.(e)
                  props.onSelect?.(item.value)
                }}
                onMouseEnter={() => {
                  props.onHoverItem?.(item.value)
                }}
                onMouseLeave={() => {
                  props.onHoverItem?.(null)
                }}
              >
                {/* Label */}
                <div className="z-10">{item.label}</div>

                {/* Indicator */}
                {active && (
                  <motion.div
                    style={
                      {
                        "--opacity": indicatorOpacity,
                        "--progress": item.progress ? transformValue(() => item.progress!.get() * 100 + "%") : 0,
                      } as any
                    }
                    layoutId="indicator"
                    className={cn(
                      "absolute opacity-[--opacity] inset-0 rounded-sm z-0 transition-colors group-active:transition-none",
                      "bg-white/10 group-active:bg-white/15",
                      "[background-image:linear-gradient(to_right,rgba(255,255,255,0.05)_calc(var(--progress)-2%),transparent_calc(var(--progress)+2%))]",
                    )}
                  ></motion.div>
                )}
              </Link>
            </motion.li>
          )
        })}
      </motion.ul>
    </LayoutGroup>
  )
}
